import { type EmailCadence, NotificationTier } from '@developerz/domain';
import { type Accessor, For, type JSX, Show } from 'solid-js';
import { t } from '../../i18n';
import { TIER_LABEL_KEY } from '../../lib/notification-labels';
import { Field } from '../Field';
import { Panel } from '../Panel';

// Delivery timing (notifications overhaul, slice 20): how often mail that can
// wait is sent. PURE PRESENTER: the cadence value, the busy flag and the
// failure copy belong to the PARENT — `NotificationTiers` owns the page's
// ONLY settings read and a separate cadence-only save mutation, so mounting
// this section a second time as a sibling brings no second observer with it.
// The cadence PUT runs through that parent's mutation; this section never
// instantiates a `useNotificationSettings` of its own.
//
// A fieldset of native radios, not a new segmented control: this app has
// none, and one `name` gives the keyboard contract for free (one tab stop,
// arrow keys inside the group).
//
// CONTROLLED, like every write surface here (`apps/dashboard/CLAUDE.md` §
// Boundary): a click must not move the dot, the server's answer does. A
// native radio checks itself before `change` fires, so the handler puts the
// dot back on the parent's choice and the parent's re-read moves it. Without
// that, a refused write would leave the refused choice looking saved,
// because nothing in the props flipped. The radios stay ENABLED while the
// write is in flight: disabling the focused one drops keyboard focus to
// `<body>`.
//
// THE FIELDSET IS OMITTED while the parent holds no cadence at all (its read
// in flight or refused). Cadence is always rendered inside
// `NotificationTiers`'s `<QueryBoundary>` (which gates on the settings
// having loaded with no error), so that branch is defensive. A stored
// cadence the page does not offer (`daily`) is still a cadence: the
// fieldset paints with no radio checked, and a choice still saves. The
// "Saving…" status follows `pending()` alone.

/**
 * The timings the page offers, in draw order. `daily` is deliberately
 * absent: owner ruling 2026-10-07 (recorded on #5697) — the API refuses it
 * with a 422 (`DAILY_CADENCE_DETAIL`, `apps/api/src/deps/notification-settings.ts`
 * `planReplace`), and an option that always fails must not be offered. The
 * dev mock mirrors the refusal (`notification-settings-mocks.ts`). The day
 * the API accepts it: add `'daily'` here — its label row below and its
 * locale strings stay banked meanwhile, and extend this section's tests.
 */
const SELECTABLE_CADENCES: readonly EmailCadence[] = ['instant', 'hourly'];

/**
 * One label per cadence the DOMAIN declares, offered or not. Closed over
 * `EmailCadence`: a cadence added to the domain fails to compile here, which
 * is the seam that forces the offer-or-refuse decision. The `daily` row is
 * rendered by no one while the API refuses `daily`; it stays so the record
 * stays total and its strings stay translated for the acceptance day.
 */
const CADENCE_LABEL_KEY: Readonly<Record<EmailCadence, string>> = {
  instant: 'notifications_settings.timing.instant',
  hourly: 'notifications_settings.timing.hourly',
  daily: 'notifications_settings.timing.daily',
};

/** The fieldset's id: the prefix of every radio id, of the hint and of the error. */
const FIELD_ID = 'notification-cadence';

type CadenceProps = {
  /**
   * Current cadence, the parent's answer, unfiltered — it may be one the
   * page does not offer. `undefined` while the parent's read is in flight
   * or refused.
   */
  readonly cadence: Accessor<EmailCadence | undefined>;
  /** True while a cadence PUT is in flight. */
  readonly pending: Accessor<boolean>;
  /**
   * Server detail or fallback sentence the parent's cadence mutation most
   * recently produced; `null` when there is nothing to show.
   */
  readonly error: Accessor<string | null>;
  /**
   * Invoked when the user picks a cadence. The dot does not follow the
   * click or this call: it moves when the parent's re-read changes
   * `cadence()`.
   */
  readonly onChange: (next: EmailCadence) => Promise<unknown> | unknown;
};

export function NotificationCadence(props: CadenceProps): JSX.Element {
  let options: HTMLDivElement | undefined;

  /** Put the dot on the PARENT's choice, whatever the click just did. */
  const repaint = (current: EmailCadence): void => {
    for (const radio of options?.querySelectorAll<HTMLInputElement>('input[type="radio"]') ?? []) {
      radio.checked = radio.value === current;
    }
  };

  return (
    <Panel title={t('notifications_settings.timing.title')} class="notification-cadence">
      <Show when={props.cadence()}>
        <Field
          as="fieldset"
          id={FIELD_ID}
          label={t('notifications_settings.timing.legend')}
          desc={t('notifications_settings.timing.hint', {
            urgent: t(TIER_LABEL_KEY[NotificationTier.Urgent]),
            heads_up: t(TIER_LABEL_KEY[NotificationTier.HeadsUp]),
          })}
          error={props.error()}
        >
          {(a11y) => (
            <div class="notification-cadence__options" ref={options} aria-busy={props.pending()}>
              <For each={SELECTABLE_CADENCES}>
                {(cadence) => (
                  <label class="notification-cadence__option">
                    <input
                      type="radio"
                      name={a11y.id}
                      id={`${a11y.id}-${cadence}`}
                      value={cadence}
                      checked={props.cadence() === cadence}
                      onChange={() => {
                        const current = props.cadence();
                        if (current === undefined) return;
                        repaint(current);
                        void props.onChange(cadence);
                      }}
                    />
                    {t(CADENCE_LABEL_KEY[cadence])}
                  </label>
                )}
              </For>
            </div>
          )}
        </Field>
      </Show>
      <Show when={props.pending()}>
        <p class="field__desc" role="status">
          {t('notifications_settings.timing.saving')}
        </p>
      </Show>
    </Panel>
  );
}
